import { motion } from 'framer-motion';
import { X } from 'lucide-react';

/** Shared full-screen profile shell used by both Search Result and Contact profiles. */
export default function ProfileShell({ children, onClose, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className="invite-profile-overlay"
      data-testid={testid}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <motion.div
        initial={{ scale: 0.97, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.97, opacity: 0 }}
        transition={{ duration: 0.18 }}
        className="invite-profile-panel"
      >
        <button
          onClick={onClose}
          data-clickable="true"
          data-testid="invite-profile-close"
          className="absolute top-4 right-4 z-10 text-[#777] hover:text-white p-1.5 border border-[#222] bg-[#080808] hover:bg-[#111] transition-colors"
          aria-label="Close profile"
        >
          <X className="w-4 h-4" />
        </button>
        {children}
      </motion.div>
    </motion.div>
  );
}

/** Shows label+value only when value is meaningful. Strips strings to detect emptiness. */
export function DetailField({ label, value, mono = false, multiline = false, link = false, testid }) {
  const display = formatValue(value);
  if (display === null) return null;
  return (
    <div data-testid={testid} className="border border-[#161616] bg-[#070707] p-3 hover:border-[#222] transition-colors">
      <p className="text-[9px] font-mono text-[#777] tracking-[0.18em] uppercase mb-1">{label}</p>
      {link ? (
        <a href={display} target="_blank" rel="noreferrer" className={`text-xs ${mono ? 'font-mono' : ''} text-[#90E0EF] hover:underline break-words block`}>{display}</a>
      ) : (
        <p className={`text-xs ${mono ? 'font-mono' : ''} text-white break-words ${multiline ? 'whitespace-pre-wrap leading-relaxed' : ''}`}>{display}</p>
      )}
    </div>
  );
}

/** Optional larger section. Hides itself if children are empty. */
export function DetailSection({ title, children, testid }) {
  const validChildren = Array.isArray(children)
    ? children.filter((c) => c !== null && c !== false && c !== undefined)
    : (children !== null && children !== false && children !== undefined ? [children] : []);
  if (!validChildren.length) return null;
  return (
    <section data-testid={testid} className="space-y-2">
      <h4 className="text-[10px] font-mono text-[#90E0EF] tracking-[0.22em] uppercase">{title}</h4>
      <div className="space-y-2">{validChildren}</div>
    </section>
  );
}

/** Long text block. Renders nothing if empty. */
export function ProseBlock({ label, children, testid }) {
  if (!hasValue(children)) return null;
  return (
    <div data-testid={testid} className="border border-[#161616] bg-[#070707] p-4">
      <p className="text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase mb-2">{label}</p>
      <p className="text-xs text-[#dcdcdc] leading-relaxed whitespace-pre-wrap">{children}</p>
    </div>
  );
}

function formatValue(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s || s === '-' || s === '—') return null;
    return s;
  }
  if (Array.isArray(v)) {
    const filtered = v.filter((x) => hasValue(x));
    return filtered.length ? filtered.join(', ') : null;
  }
  if (typeof v === 'number') return String(v);
  return String(v);
}

function hasValue(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return !!v.trim() && v.trim() !== '-' && v.trim() !== '—';
  if (Array.isArray(v)) return v.some(hasValue);
  return true;
}
